import { pPrToCss, rPrToCss, rStyleOf } from '../ooxmlCss.js'
import { createMeasure } from './chartText.js'
import { layoutChart } from './chartLayout.js'
import { chartSvgSpec } from './chartSvg.js'
import { deriveChartModel } from './chartDerive.js'

/**
 * A chart rawBlock (a Word paragraph whose drawing is a c:chart) as the
 * canvas shows it: the paragraph — its pPr, its style, any text runs around
 * the chart such as a "Joonis 1. …" caption — with the chart drawn in SVG
 * from the presentation payload's chart model, at the drawing's own size.
 *
 * Read-only by construction: this only renders the verbatim XML; the node
 * keeps it untouched for the compiler. Returns null whenever something in
 * the paragraph cannot be drawn faithfully, and the caller keeps the card.
 */

const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main'

const KNOWN_NAMESPACES = {
  w: W_NS,
  wp: 'http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing',
  a: 'http://schemas.openxmlformats.org/drawingml/2006/main',
  c: 'http://schemas.openxmlformats.org/drawingml/2006/chart',
  r: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships',
  mc: 'http://schemas.openxmlformats.org/markup-compatibility/2006',
}

let domParser = null
let sharedMeasure = null

function measurer() {
  sharedMeasure ??= createMeasure()
  return sharedMeasure
}

/**
 * Forget the widths measured so far. A chart laid out before its face had
 * loaded (Carlito for Calibri, fonts.css) was measured in the fallback
 * face, and the cache would hand those widths to every later layout of the
 * same text: its redraw once the face is in, and other charts all session.
 */
export function forgetChartWidths() {
  sharedMeasure = null
}

/** Parse a verbatim paragraph, declaring whatever prefixes it uses. */
function parseParagraph(xml) {
  if (typeof DOMParser === 'undefined' || !xml) return null
  const prefixes = new Set()
  for (const match of xml.matchAll(/<\/?([A-Za-z][\w.-]*):|\s([A-Za-z][\w.-]*):[\w.-]+=/g)) {
    const prefix = match[1] ?? match[2]
    if (prefix && prefix !== 'xmlns' && prefix !== 'xml') prefixes.add(prefix)
  }
  const declarations = [...prefixes].map((prefix) => `xmlns:${prefix}="${KNOWN_NAMESPACES[prefix] ?? `urn:studio:${prefix}`}"`).join(' ')
  try {
    domParser ??= new DOMParser()
    const doc = domParser.parseFromString(`<root ${declarations}>${xml}</root>`, 'text/xml')
    if (doc.getElementsByTagName('parsererror').length) return null
    return doc.documentElement.firstElementChild
  } catch {
    return null
  }
}

const localName = (element) => element.localName.replace(/^.*:/, '')

const serializer = () => new XMLSerializer()

/** The chart a rawBlock draws: its relationship id and frame size (EMU), or null. */
export function chartReference(xml) {
  const rId = /<c:chart\b[^>]*\br:id="([^"]+)"/.exec(xml ?? '')?.[1]
  if (!rId) return null
  const extent = /<wp:extent\b[^>]*\bcx="(\d+)"[^>]*\bcy="(\d+)"/.exec(xml)
  return { rId, cx: extent ? Number(extent[1]) : null, cy: extent ? Number(extent[2]) : null }
}

function onOff(element, name) {
  const property = [...element.children].find((child) => localName(child) === name)
  if (!property) return null
  const value = property.getAttributeNS(W_NS, 'val') || property.getAttribute('w:val') || ''
  return !['0', 'false', 'none', 'off'].includes(value.toLowerCase())
}

/** A run's rPr as CSS, toggles included (the marks draw them elsewhere). */
function runCss(rPr) {
  if (!rPr) return { style: '', rStyle: null }
  const xml = serializer().serializeToString(rPr)
  const css = [rPrToCss(xml)]
  if (onOff(rPr, 'b')) css.push('font-weight: bold')
  if (onOff(rPr, 'b') === false) css.push('font-weight: normal')
  if (onOff(rPr, 'i')) css.push('font-style: italic')
  if (onOff(rPr, 'i') === false) css.push('font-style: normal')
  const decorations = []
  if (onOff(rPr, 'u')) decorations.push('underline')
  if (onOff(rPr, 'strike')) decorations.push('line-through')
  if (decorations.length) css.push(`text-decoration: ${decorations.join(' ')}`)
  return { style: css.filter(Boolean).join('; '), rStyle: rStyleOf(xml) }
}

/**
 * A text run: studio-run puts its glyphs on Word's baseline (studio.css,
 * lineMetrics.js). A run of spaces only takes no line height, as in Word:
 * chart 33's space between the chart and its wrapped caption hung at the
 * end of the chart's line and deepened it by its descent.
 */
const runSpec = (style, rStyle, text) => ['span', { class: /\S/.test(text) ? 'studio-run' : undefined, style: style || undefined, 'data-r-style': rStyle ?? undefined }, text]

/**
 * The paragraph's inline content → specs, or null when it holds anything
 * the canvas cannot draw. `drawChart(rId, cx, cy)` returns the chart spec.
 */
function inlineSpecs(paragraph, drawChart) {
  const out = []
  let field = 'none' // none | code | result
  let drewChart = false

  const visit = (container) => {
    for (const child of container.children) {
      const name = localName(child)
      if (['pPr', 'bookmarkStart', 'bookmarkEnd', 'proofErr', 'permStart', 'permEnd', 'commentRangeStart', 'commentRangeEnd'].includes(name)) continue
      if (['hyperlink', 'smartTag', 'ins', 'customXml', 'fldSimple', 'sdtContent'].includes(name)) {
        if (!visit(child)) return false
        continue
      }
      if (name === 'sdt') {
        const content = [...child.children].find((part) => localName(part) === 'sdtContent')
        if (content && !visit(content)) return false
        continue
      }
      if (name === 'del' || name === 'moveFrom') continue
      if (name !== 'r') return false
      const rPr = [...child.children].find((part) => localName(part) === 'rPr') ?? null
      const { style, rStyle } = runCss(rPr)
      const pieces = []
      for (const part of child.children) {
        const kind = localName(part)
        if (kind === 'rPr' || kind === 'lastRenderedPageBreak' || kind === 'instrText') continue
        if (kind === 'fldChar') {
          const type = part.getAttributeNS(W_NS, 'fldCharType') || part.getAttribute('w:fldCharType')
          field = type === 'begin' ? 'code' : type === 'separate' ? 'result' : 'none'
          continue
        }
        if (field === 'code') continue
        if (kind === 't') pieces.push(part.textContent)
        else if (kind === 'tab') pieces.push('\t')
        else if (kind === 'br') {
          const type = part.getAttributeNS(W_NS, 'type') || part.getAttribute('w:type')
          if (type === 'page' || type === 'column') continue
          if (pieces.length) out.push(runSpec(style, rStyle, pieces.join('')))
          pieces.length = 0
          out.push(['br'])
        } else if (kind === 'noBreakHyphen') pieces.push('‑')
        else if (kind === 'softHyphen') continue
        else if (kind === 'sym') return false
        else if (kind === 'drawing') {
          const xml = serializer().serializeToString(part)
          const reference = chartReference(xml)
          const spec = reference ? drawChart(reference) : null
          if (!spec) return false
          if (pieces.length) out.push(runSpec(style, rStyle, pieces.join('')))
          pieces.length = 0
          out.push(spec)
          drewChart = true
        } else {
          return false
        }
      }
      if (pieces.length) out.push(runSpec(style, rStyle, pieces.join('')))
    }
    return true
  }

  if (!visit(paragraph) || !drewChart) return null
  return out
}

/**
 * The spec of a chart rawBlock, or null (draw the card). `charts` is the
 * presentation payload's chart map (relationship id → model); `edit` the
 * block's chartEdit override (RED-317 PR 3d-A), drawn as the compiler will
 * write it (chartDerive.js).
 */
export function chartBlockSpec(xml, charts, label = '', edit = null) {
  if (!charts) return null
  const paragraph = parseParagraph(xml)
  if (!paragraph || localName(paragraph) !== 'p') return null

  const drawChart = (reference) => {
    const served = charts[reference.rId]
    if (!served || served.version !== 1 || (served.unsupported ?? []).length) return null
    const model = deriveChartModel(served, edit)
    // The drawing's own extent wins: the model's layout is in fractions.
    const sized = reference.cx && reference.cy ? { ...model, widthPt: reference.cx / 12700, heightPt: reference.cy / 12700 } : model
    const layout = layoutChart(sized, measurer())
    return ['span', { class: 'studio-chart', contenteditable: 'false' }, chartSvgSpec(layout, label)]
  }

  const inline = inlineSpecs(paragraph, drawChart)
  if (!inline) return null

  const pPr = [...paragraph.children].find((child) => localName(child) === 'pPr') ?? null
  const pPrXml = pPr ? serializer().serializeToString(pPr) : null
  const pStyle = pPr ? [...pPr.children].find((child) => localName(child) === 'pStyle') : null
  const styleId = pStyle ? pStyle.getAttributeNS(W_NS, 'val') || pStyle.getAttribute('w:val') : null
  const css = pPrXml ? pPrToCss(pPrXml) : ''

  return [
    'div',
    { class: 'studio-chart-block', 'data-hint': 'chart', 'data-testid': 'studio-chart' },
    // studio-run-lines: the paragraph's strut adds nothing to a line of runs
    // or a drawing, as with every textblock (textRuns.js).
    ['p', { class: 'studio-run-lines', 'data-style-id': styleId || undefined, style: css || undefined }, ...inline],
  ]
}

/** The text styles a chart model draws with, for font loading. */
export function chartFonts(model) {
  const fonts = new Set()
  const walk = (value) => {
    if (!value || typeof value !== 'object') return
    if (typeof value.font === 'string' && typeof value.size === 'number') fonts.add(value)
    for (const child of Object.values(value)) walk(child)
  }
  walk(model)
  return [...fonts]
}
